import {skipToken} from '@reduxjs/toolkit/query';
import {addDays, subDays} from 'date-fns';

import {isNil} from 'ramda';
import {isValidDate} from 'ramda-adjunct';

import {
  type RentalCalendarEventResponseBody,
  type RentalOrderResponseBody,
  useListRentalCalendarEventsQuery,
} from '@dms/api/rental';

import {getApiDateString, Nullish} from 'shared/web';

import {eventsOverlap} from '../utils/eventsOverlap';
import {mapCalendarEventsFromApi} from '../utils/mapCalendarEventsFromApi';

export function useOverlappingRentalEvents(
  vehicleId: string | Nullish,
  from: Date | Nullish,
  to: Date | Nullish
) {
  const period = isValidDate(from) && isValidDate(to) && from < to ? {start: from, end: to} : null;

  // `currentData`, not `data`: `data` keeps the previous vehicle's result across an argument change
  const {currentData, isFetching} = useListRentalCalendarEventsQuery(
    isNil(vehicleId) || isNil(period)
      ? skipToken
      : {
          // Padded a day each side because `getApiDateString` drops the time
          from: getApiDateString(subDays(period.start, 1)),
          to: getApiDateString(addDays(period.end, 1)),
          vehicleId: [vehicleId],
        }
  );

  const events =
    isNil(vehicleId) || isNil(period)
      ? []
      : mapCalendarEventsFromApi(toScheduledEvents(currentData), undefined).filter(
          (event) =>
            !RELEASED_STATES.includes(event.rentalOrder?.state) && eventsOverlap(event, period)
        );

  return {events, isFetching};
}

// An order is booked over its scheduled period even when collected late, so the handover is ignored
const toScheduledEvents = (events: RentalCalendarEventResponseBody[] | Nullish) =>
  (events ?? []).map((event) => ({...event, vehicleRent: null, vehicleReturn: null}));

// Cancelled and closed orders no longer hold the vehicle; a block has no state, so it always counts
const RELEASED_STATES: Array<RentalOrderResponseBody['state'] | undefined> = ['canceled', 'closed'];
